import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// react admin
import {
  Datagrid,
  DateField,
  List,
  NumberField,
  TextField,
  ReferenceField,
  TopToolbar,
  useListContext,
  useListController,
} from "react-admin";
// mui
import {
  Button,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  Select,
  InputLabel,
  FormControl,
  TextField as MuiTextField,
  useTheme,
  useMediaQuery,
  CircularProgress
} from "@mui/material";
// mui icon
import GetAppIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import BackupTableIcon from "@mui/icons-material/BackupTable";
// components
import { Summary } from "./Summary/Summary";
import CustomPagination from "../../Components/CustomPagination";
// loader
import { Loader } from "../Loader";
// utils
import { rechargeRecordsExports } from "../../Utilities/allExports";
// xlsx export
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { Parse } from "parse";
import Autocomplete from "@mui/material/Autocomplete";

Parse.initialize(
  process.env.REACT_APP_APP_ID,
  null,
  process.env.REACT_APP_MASTER_KEY
);
Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
Parse.serverURL = process.env.REACT_APP_SERVER_URL;

const CustomFilter = () => {
  const { setFilters } = useListContext();

  const [selectedField, setSelectedField] = useState("username");
  const [filterValue, setFilterValue] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // 🔍 Manager search
  const handleSearch = async (inputValue) => {
    if (!inputValue) {
      setUserOptions([]);
      return;
    }
    setLoadingUsers(true);
    try {
      const query = new Parse.Query(Parse.User);
      query.limit(20);
      query.select(["username", "role"]);
      query.include("role");
      query.matches("username", inputValue, "i");

      // exclude Player role
      const roleQuery = new Parse.Query(Parse.Role);
      roleQuery.notEqualTo("name", "Player");
      query.matchesQuery("role", roleQuery);

      const results = await query.find();
      const users = results.map((u) => ({
        id: u.id,
        username: u.get("username"),
        role: u.get("role")?.get("name") || "Unknown",
      }));
      setUserOptions(users);
    } catch (err) {
      console.error("Manager search failed:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleApply = () => {
    if (!filterValue || filterValue === "") return;
    setFilters({ [selectedField]: filterValue }, {});
  };

  const handleClear = () => {
    setSelectedField("username");
    setFilterValue("");
    setFilters({}, {});
    setUserOptions([]);
  };

  return (
    <Box
      display={{ xs: "block", width: "100%", sm: "flex" }}
      alignItems="center"
      gap={2}
    >
      <Box display="flex" width="max-content">
        <FormControl>
          <InputLabel>Filter By</InputLabel>
          <Select
            size="small"
            value={selectedField}
            label="Search By"
            onChange={(e) => {
              setSelectedField(e.target.value);
              setFilterValue("");
            }}
            sx={{ marginRight: "10px", minWidth: 120 }}
          >
            <MenuItem value="username">User</MenuItem>
            <MenuItem value="manager">Role</MenuItem>
          </Select>
        </FormControl>

        {selectedField === "manager" ? (
          <Autocomplete
            size="small"
            sx={{ minWidth: 220 }}
            options={userOptions}
            getOptionLabel={(option) =>
              `${option.username} (${option.role})`
            }
            loading={loadingUsers}
            onInputChange={(e, newValue) => handleSearch(newValue)}
            onChange={(e, newValue) =>
              setFilterValue(newValue ? newValue.id : "")
            }
            renderInput={(params) => (
              <MuiTextField
                {...params}
                label="Search Manager"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        ) : (
          <MuiTextField
            size="small"
            variant="outlined"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            placeholder="Enter value"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleApply();
              }
            }}
          />
        )}
      </Box>

      <Box display="flex">
        <Button
          variant="contained"
          sx={{
            marginTop: "10px",
            marginBottom: "10px",
            marginRight: "10px",
            background: "#FFC107",
            color: "#0A0A0A",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#FFA000",
            },
          }}
          onClick={handleApply}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          sx={{
            marginTop: "10px",
            marginBottom: "10px",
            marginRight: "10px",
            outline: "1px solid #FFC107",
            color: "white",
            textTransform: "none",
          }}
          onClick={handleClear}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
};

const SummaryChild = ({ setSummaryData, setFilters }) => {
  const { isFetching, filterValues, isLoading } = useListContext();

  // Pass filters to parent when they change
  useEffect(() => {
    setFilters(filterValues);
  }, [filterValues, setFilters]);

  // Function to fetch and process data
  const fetchRechargeSummary = async () => {
    try {
      const filter = filterValues;
      let query = new Parse.Query("Transactions");
      query.equalTo("txn_type", 0);
      query.limit(10000);

      // Exclude current logged-in user
      const currentUser = Parse.User.current();
      if (currentUser) {
        query.notEqualTo("user", currentUser);
      }

      // Apply filters
      if (filter.username) {
        Object.keys(filter).forEach((f) => {
          if (typeof filter[f] === "string") {
            query.matches(f, filter[f], "i");
          } else {
            query.equalTo(f, filter[f]);
          }
        });
      } else   if (filter.manager) {
        const userQuery = new Parse.Query(Parse.User);
        userQuery.matches("objectId", filter.manager, "i");
        const managers = await userQuery.find({ useMasterKey: true });
    
        if (managers.length > 0) {
          const managerPointers = managers.map((m) => m.toPointer());
          query.containedIn("manager", managerPointers);
        } else {
          // Force no results if no match
          //query.equalTo("objectId", "__no_such_id__");
        }
      }

      const count = await query.count();
      const filteredTransactions = await query.find();

      const totalRechargeAmount = filteredTransactions
        .reduce((sum, txn) => sum + (txn.get("amount") || 0), 0)
        .toFixed(2);

      const averageRechargeAmount =
        count > 0 ? (totalRechargeAmount / count)?.toFixed(2) : "0.00";

      const userMap = new Map();
      filteredTransactions.forEach((txn) => {
        const userId = txn.get("user")?.id;
        const amount = txn.get("amount") || 0;
        if (!userId) return;

        userMap.set(userId, (userMap.get(userId) || 0) + amount);
      });

      let topUserId = null;
      let topUserAmount = 0;
      userMap.forEach((amount, userId) => {
        if (amount > topUserAmount) {
          topUserAmount = amount;
          topUserId = userId;
        }
      });

      const topUser = topUserId
        ? await new Parse.Query(Parse.User).get(topUserId)
        : null;

      setSummaryData({
        totalTransactions: count,
        totalRechargeAmount: parseFloat(totalRechargeAmount).toFixed(2),
        averageRechargeAmount: parseFloat(averageRechargeAmount).toFixed(2),
        topUser: topUser
          ? {
              name: topUser.get("username"),
              totalRechargeAmount: parseFloat(topUserAmount).toFixed(2),
            }
          : null,
        isFetching,
        isLoading,
      });
    } catch (error) {
      const isInvalidSession =
        error?.code === 209 ||
        error?.message?.toLowerCase().includes("invalid session token");

      if (isInvalidSession) {
        console.warn("Invalid session detected. Logging out...");

        try {
          await Parse.User.logOut();
        } catch (logoutError) {
          console.error("Logout failed:", logoutError);
        }

        localStorage.clear();

        return;
      }

      // Optional: Handle other errors
      console.error("Error fetching recharge summary:", error);
    }
  };

  // Call fetchRechargeSummary function whenever filterValues change
  useEffect(() => {
    if (filterValues) {
      fetchRechargeSummary();
    }
  }, [filterValues]);

  return null; // Nothing is rendered from this component itself
};

export const RechargeList = () => {
  const { isLoading, total, page, perPage, setPage, setPerPage } =
    useListController();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [isExporting, setIsExporting] = useState(false);

  const navigate = useNavigate();

  const currentUserData = localStorage.getItem(
    `Parse/${process.env.REACT_APP_APP_ID}/currentUser`
  );

  if (!currentUserData) {
    navigate("/login");
  }

  const [summaryData, setSummaryData] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [filters, setFilters] = useState({});

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // export the data in pdf formate
  const handleExportPDF = async () => {
    console.log("*** Generating PDF with filters:", filters);
  };

  // export the data in xls formate
  const handleExportXLS = async () => {
    try {
      setIsExporting(true);

      const exportData = await rechargeRecordsExports(filters);

      if (!exportData || !exportData?.data || exportData?.data?.length === 0) {
        console.warn("No data to export.");
        return;
      }

      const selectedFields = exportData.data.map((item) => ({
        Date: new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false, // Set to true for AM/PM format
        }).format(new Date(item.txn_datetime)),
        Amount: item.amount,
        Remarks: item.remarks,
        User: item.username,
        Manager: item.manager.name,
      }));

      const worksheet = XLSX.utils.json_to_sheet(selectedFields);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Recharge Records");
      const xlsData = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([xlsData], { type: "application/octet-stream" }),
        "RechargeRecords.xlsx"
      );
    } catch (error) {
      console.error("Error fetching user engagement:", error);
    } finally {
      setIsExporting(false);
      handleMenuClose();
    }
  };

  const listActions = (
    <TopToolbar>
      <Button
        variant="contained"
        startIcon={<GetAppIcon />}
        sx={{
          background: "#FFC107",
          color: "#0A0A0A",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#FFA000",
          },
        }}
        onClick={handleMenuOpen}
      >
        Export
      </Button>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        sx={{ marginTop: 1 }}
      >
        {/* <MenuItem onClick={handleExportPDF}>
                <ListItemIcon>
                  <PictureAsPdfIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  PDF file
                </Typography>
              </MenuItem> */}

        <MenuItem onClick={handleExportXLS} disabled={isExporting}>
          <ListItemIcon>
            <BackupTableIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {isExporting ? "Exporting..." : "Excel file"}
          </Typography>
        </MenuItem>
      </Menu>
    </TopToolbar>
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 80px)",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: "#242424",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Summary summaryData={summaryData} />
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pt: 2 }}>
          <Box
            sx={{
              mt: 2,
              backgroundColor: "#242424",
              borderRadius: 2,
              boxShadow: "none !important",
              padding: isSmallScreen ? { xs: 0, sm: 0 } : { xs: 1.5, sm: 2 },
            }}
          >
            <List
              title="Recharge Records"
              actions={listActions}
              filters={<CustomFilter />}
              pagination={false}
              sort={{ field: "txn_datetime", order: "DESC" }}
              sx={{
                "& .RaList-actions": {
                  flexWrap: "nowrap",
                },
                "& .RaFilterFormInput-spacer": { display: "none" },
              }}
            >
              <SummaryChild
                setSummaryData={setSummaryData}
                setFilters={setFilters}
              />

              <Box
                style={{
                  width: "100%",
                  overflowX: "auto",
                }}
              >
                <Box
                  style={{
                    width: "100%",
                    position: isSmallScreen ? "absolute" : "relative",
                  }}
                >
                  <Datagrid bulkActionButtons={false}>
                    <DateField source="txn_datetime" label="Date" showTime />
                    <NumberField
                      source="amount"
                      label="Amount"
                      textAlign="left"
                    />
                    <TextField source="remarks" />
                    <ReferenceField
                      source="user.id"
                      reference="users"
                      label="User"
                      emptyText="Missing User"
                      link={false}
                      sortBy="user.username"
                    >
                      <TextField source="username" />
                    </ReferenceField>
                    <ReferenceField
                      source="manager.id"
                      reference="users"
                      label="Manager"
                      emptyText="Missing User"
                      link={false}
                      sortBy="manager.username"
                    >
                      <TextField source="username" />
                    </ReferenceField>
                  </Datagrid>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100% !important",
                      margin: "16px 0px",
                    }}
                  >
                    <CustomPagination
                      page={page}
                      perPage={perPage}
                      total={total}
                      setPage={setPage}
                      setPerPage={setPerPage}
                    />
                  </Box>
                </Box>
              </Box>
            </List>
          </Box>
        </Box>
      </Box>
    </React.Fragment>
  );
};
